import _ from 'lodash';
import { Store } from '@/core/flux.service';
import { HOME_SCREEN_TABLE_TYPE, TrainingPromptDisplay } from '@/homescreen/homescreen.constants';
import { HOME_SCREEN_TABS } from '@/main/app.constants';
import type { PersistenceLevel } from '@/core/flux.service';
import type { HomeScreenItemType } from '@/homescreen/HomeScreenTable.organism';
import type { PageSizeByTableItem, SortByTableItem } from '@/homescreen/homescreen.constants';
import type { SearchItemLocation } from '@/main/app.constants';
import type {
  GetFoldersFilterEnum as FilterEnum,
  GetFoldersSearchFieldsEnum as SearchFieldsEnum,
} from '@seeqdev/api/endpoint/FoldersApi';

type HomeScreenTableKey = string;

interface HomeScreenFolder {
  id: string;
  name: string;
  subfolders?: HomeScreenFolder[];
  [key: string]: unknown;
}

interface PageNumberEntry {
  key: HomeScreenTableKey;
  pageNumber: number;
}

interface ItemsByTableEntry {
  key: HomeScreenTableKey;
  items: HomeScreenItemType[];
  totalResults: number;
}

interface SetPageNumberPayload {
  table: HomeScreenTableKey;
  pageNumber: number;
}

interface SetTableSortPayload {
  table: HomeScreenTableKey;
  sortProperty: string;
  sortAsc: boolean;
}

interface SetTableItemsPayload {
  table: HomeScreenTableKey;
  items: HomeScreenItemType[];
  totalResults?: number;
}

interface Breadcrumb {
  id: string;
  name?: string;
  [key: string]: unknown;
}

type HomeScreenSearchParamValue =
  | string
  | number
  | boolean
  | string[]
  | string[][]
  | FilterEnum
  | SearchFieldsEnum
  | SearchFieldsEnum[]
  | SearchItemLocation
  | undefined;

export interface HomeScreenSearchParams {
  sortOrder?: string;
  onlyPinned?: boolean;
  folderId?: string;
  filter?: FilterEnum | SearchItemLocation;
  limit?: number;
  offset?: number;
  types?: string[] | string[][];
  textSearch?: string;
  searchFields?: string | string[] | SearchFieldsEnum[];
  isExact?: boolean;
  isArchived?: boolean;
  creatorIds?: string[];
  ownerIds?: string[];
  [key: string]: HomeScreenSearchParamValue;
}

interface TabFolder {
  id?: string;
  [key: string]: unknown;
}

interface HomeScreenState {
  isExact: boolean;
  customLinksExpanded: boolean;
  currentTab: HOME_SCREEN_TABS;
  previousTab: HOME_SCREEN_TABS;
  currentFolderId: string;
  currentlyLoading: string;
  tabFolders: Record<string, TabFolder | undefined>;
  pageNumberByTable: PageNumberEntry[];
  expandedFolderIds: string[];
  folderTree: HomeScreenFolder[];
  searchParams: HomeScreenSearchParams;
  loadingTable: HomeScreenTableKey[];
  itemsByTable: ItemsByTableEntry[];
  breadcrumbs: Breadcrumb[];
  isLoadingBreadcrumbs: boolean;
  sortByTable: SortByTableItem[];
  pageSizeByTable: PageSizeByTableItem[];
  trainingPromptDisplay: TrainingPromptDisplay;
  displayedAddOnIdentifier: string;
  branchCode: string | null;
}

type HomeScreenDehydratedState = Pick<
  HomeScreenState,
  'pageSizeByTable' | 'trainingPromptDisplay' | 'customLinksExpanded'
>;

export class HomeScreenStore extends Store {
  persistenceLevel: PersistenceLevel = 'WORKBENCH';
  static readonly storeName = 'sqHomeScreenStore';

  initialize() {
    const initialState: HomeScreenState = {
      isExact: false,
      customLinksExpanded: true,
      currentTab: HOME_SCREEN_TABS.HOME,
      previousTab: HOME_SCREEN_TABS.HOME,
      currentFolderId: '',
      currentlyLoading: '',
      tabFolders: {},
      pageNumberByTable: [],
      expandedFolderIds: [],
      folderTree: [],
      searchParams: {},
      loadingTable: [],
      itemsByTable: [],
      breadcrumbs: [],
      isLoadingBreadcrumbs: false,
      sortByTable: [],
      pageSizeByTable: [
        { key: HOME_SCREEN_TABLE_TYPE.TAB, size: 15 },
        { key: HOME_SCREEN_TABLE_TYPE.SEARCH, size: 15 },
        { key: HOME_SCREEN_TABLE_TYPE.PINNED, size: 5 },
        { key: HOME_SCREEN_TABLE_TYPE.RECENT, size: 5 },
      ],
      trainingPromptDisplay: TrainingPromptDisplay.HOME_SCREEN,
      displayedAddOnIdentifier: '',
      branchCode: null,
    };

    this.state = this.immutable(initialState);
  }

  get currentTab(): HOME_SCREEN_TABS {
    return this.state.get('currentTab');
  }

  get previousTab(): HOME_SCREEN_TABS {
    return this.state.get('previousTab');
  }

  get currentFolderId(): string {
    return this.state.get('currentFolderId');
  }

  get tabFolders(): Record<string, TabFolder | undefined> {
    return this.state.get('tabFolders');
  }

  get expandedFolderIds(): string[] {
    return this.state.get('expandedFolderIds');
  }

  get folderTree(): HomeScreenFolder[] {
    return this.state.get('folderTree');
  }

  get isLoadingBreadcrumbs(): boolean {
    return this.state.get('isLoadingBreadcrumbs');
  }

  get breadcrumbs(): Breadcrumb[] {
    return this.state.get('breadcrumbs');
  }

  get searchParams(): HomeScreenSearchParams {
    return this.state.get('searchParams');
  }

  get isExact(): boolean {
    return this.state.get('isExact');
  }

  get trainingPromptDisplay(): TrainingPromptDisplay {
    return this.state.get('trainingPromptDisplay');
  }

  get branchCode(): string | null {
    return this.state.get('branchCode');
  }

  get customLinksExpanded(): boolean {
    return this.state.get('customLinksExpanded');
  }

  get displayedAddOnIdentifier(): string {
    return this.state.get('displayedAddOnIdentifier');
  }

  getPageNumberForTable(table?: HomeScreenTableKey): number {
    const pageNumbers = this.state.get('pageNumberByTable') as PageNumberEntry[];
    const entry = pageNumbers.find((item) => item.key === table);
    return entry?.pageNumber ?? 1;
  }

  getSortForTable(table?: HomeScreenTableKey): SortByTableItem | undefined {
    const sortByTable = this.state.get('sortByTable') as SortByTableItem[];
    return sortByTable.find((item) => item.key === table);
  }

  getPageSizeByTable(table?: HomeScreenTableKey): number {
    const pageSizes = this.state.get('pageSizeByTable') as PageSizeByTableItem[];
    const entry = pageSizes.find((item) => item.key === table);
    return entry?.size ?? 15;
  }

  getItemsForTable(table?: HomeScreenTableKey): HomeScreenItemType[] | null {
    const itemsByTable = this.state.get('itemsByTable') as ItemsByTableEntry[];
    const entry = itemsByTable.find((item) => item.key === table);
    return entry ? entry.items : null;
  }

  getItemTotalForTable(table?: HomeScreenTableKey): number {
    const itemsByTable = this.state.get('itemsByTable') as ItemsByTableEntry[];
    const entry = itemsByTable.find((item) => item.key === table);
    return entry?.totalResults ?? 0;
  }

  isTableLoading(table: HomeScreenTableKey): boolean {
    return _.indexOf(this.state.get('loadingTable'), table) > -1;
  }

  private folderIndex(folders: HomeScreenFolder[]): Record<string, HomeScreenFolder> {
    const index: Record<string, HomeScreenFolder> = {};
    const indexFolders = (foldersToIndex?: HomeScreenFolder[]) => {
      _.forEach(foldersToIndex ?? [], (folder) => {
        index[_.toLower(folder.id)] = folder;
        indexFolders(folder.subfolders);
      });
    };

    indexFolders(folders);

    return index;
  }

  private sortFolderTree(folders: HomeScreenFolder[]): HomeScreenFolder[] {
    return _.sortBy(folders, [(folder) => (folder.name ?? '').toLowerCase(), 'name']).map((folder) => ({
      ...folder,
      subfolders: folder.subfolders ? this.sortFolderTree(folder.subfolders) : folder.subfolders,
    }));
  }

  private setPageNumberForTable({ pageNumber, table }: SetPageNumberPayload) {
    const pageNumbers = this.state.get('pageNumberByTable') as PageNumberEntry[];
    const idx = pageNumbers.findIndex((entry) => entry.key === table);
    const cursor = this.state.select('pageNumberByTable', idx);

    if (cursor.exists()) {
      cursor.merge({ key: table, pageNumber });
    } else {
      this.state.set('pageNumberByTable', [...this.state.get('pageNumberByTable'), { key: table, pageNumber }]);
    }
  }

  protected readonly handlers = {
    SET_CURRENT_HOME_SCREEN_TAB: ({ tab }: { tab: HOME_SCREEN_TABS }) => {
      this.state.set('currentTab', tab);
    },
    SET_PREVIOUS_HOME_SCREEN_TAB: ({ tab }: { tab: HOME_SCREEN_TABS }) => {
      this.state.set('previousTab', tab);
    },
    SET_CURRENT_HOME_SCREEN_FOLDER: ({ folderId }: { folderId: string }) => {
      this.state.set('currentFolderId', folderId);
    },
    SET_TAB_FOLDER: ({ key, folder }: { key: HomeScreenTableKey; folder: TabFolder }) => {
      this.state.set('tabFolders', {
        ...this.state.get('tabFolders'),
        [key]: folder,
      });
    },
    ADD_EXPANDED_FOLDER_IDS: ({ expandedFolderIds }: { expandedFolderIds: string[] }) => {
      const ids = this.state.get('expandedFolderIds');
      this.state.set('expandedFolderIds', _.uniq([...ids, ...expandedFolderIds]));
    },
    REMOVE_EXPANDED_FOLDER_IDS: ({ collapsedFolderIds }: { collapsedFolderIds: string[] }) => {
      this.state.set('expandedFolderIds', _.without(this.state.get('expandedFolderIds'), ...collapsedFolderIds));
    },
    MERGE_HOME_SCREEN_FOLDER_TREE: (payload: { tree?: HomeScreenFolder[] }) => {
      const tree = _.cloneDeep(this.state.get('folderTree')) as HomeScreenFolder[];
      const index = this.folderIndex(tree);
      const incomingTree = Array.isArray(payload.tree) ? payload.tree : [];
      for (const folder of incomingTree) {
        const existing = index[_.toLower(folder.id)];
        if (existing) {
          _.assign(existing, _.omit(folder, ['subfolders']));
          if ('subfolders' in folder) {
            existing.subfolders = folder.subfolders ?? [];
          }
        } else {
          tree.push(folder);
        }
      }
      this.state.set('folderTree', this.sortFolderTree(tree));
    },
    REPLACE_HOME_SCREEN_FOLDER_CHILDREN: (payload: { folderId: string; subfolders: HomeScreenFolder[] }) => {
      const tree = _.cloneDeep(this.state.get('folderTree')) as HomeScreenFolder[];
      const index = this.folderIndex(tree);
      const existing = index[_.toLower(payload.folderId)];

      if (!existing) {
        return;
      }

      existing.subfolders = payload.subfolders ?? [];

      this.state.set('folderTree', this.sortFolderTree(tree));
    },
    SET_HOME_SCREEN_TABLE_SORT: (payload: SetTableSortPayload) => {
      const sortByTable = this.state.get('sortByTable') as SortByTableItem[];
      const idx = _.findIndex(sortByTable, {
        key: payload.table,
      });
      const cursor = this.state.select('sortByTable', idx);
      if (cursor.exists()) {
        cursor.merge(_.omit(payload, 'table'));
      } else {
        this.state.set('sortByTable', [
          ...sortByTable,
          { key: payload.table, sortProperty: payload.sortProperty, sortAsc: payload.sortAsc },
        ]);
      }
    },
    SET_HOME_SCREEN_TABLE_LOADING: ({ table, isLoading }: { table: HomeScreenTableKey; isLoading: boolean }) => {
      const currentlyLoading: HomeScreenTableKey[] = _.without(this.state.get('loadingTable'), table);
      this.state.set('loadingTable', isLoading ? [...currentlyLoading, table] : currentlyLoading);
    },
    SET_HOME_SCREEN_ITEMS_FOR_TABLE: ({ table, items, totalResults = 0 }: SetTableItemsPayload) => {
      const itemsByTable = this.state.get('itemsByTable') as ItemsByTableEntry[];
      const idx = itemsByTable.findIndex((entry) => entry.key === table);
      const payload = { items, totalResults };
      if (idx > -1) {
        this.state.merge(['itemsByTable', idx], payload);
      } else {
        this.state.push('itemsByTable', { key: table, ...payload });
      }

      const pageNumbers = this.state.get('pageNumberByTable') as PageNumberEntry[];
      const currentPageNumber = pageNumbers.find((entry) => entry.key === table)?.pageNumber ?? 1;
      if (items.length === 0 && currentPageNumber > 1) {
        this.setPageNumberForTable({ table, pageNumber: currentPageNumber - 1 });
      }
    },
    SET_HOME_SCREEN_PAGE_NUMBER: this.setPageNumberForTable,
    SET_HOME_SCREEN_SEARCH_PARAMS: ({ searchParams }: { searchParams: HomeScreenSearchParams }) => {
      this.state.set('searchParams', searchParams);
    },
    SET_PAGE_SIZE_FOR_TABLE: ({ table, size }: { table: HomeScreenTableKey; size: number }) => {
      const idx = _.findIndex(this.state.get('pageSizeByTable'), {
        key: table,
      });
      const cursor = this.state.select('pageSizeByTable', idx);
      if (cursor.exists()) {
        cursor.merge({ key: table, size });
      } else {
        this.state.set('pageSizeByTable', [...this.state.get('pageSizeByTable'), { key: table, size }]);
      }
    },
    CLEAR_HOME_SCREEN_ITEMS: () => {
      this.state.set('folderTree', []);
      this.state.set('expandedFolderIds', []);
      this.state.set('itemsByTable', []);
    },
    SET_BREADCRUMBS: ({ crumbs }: { crumbs: Breadcrumb[] }) => {
      this.state.set('breadcrumbs', crumbs);
    },
    SET_IS_LOADING_BREADCRUMBS: ({ isLoading }: { isLoading: boolean }) => {
      this.state.set('isLoadingBreadcrumbs', isLoading);
    },
    CLEAR_BREADCRUMBS: () => {
      this.state.set('breadcrumbs', []);
    },
    RESET_HOME_SCREEN_STORE: () => {
      const stateToSave = _.pick(this.state.serialize(), ['pageSizeByTable']);
      this.state.merge({
        ...stateToSave,
        pageNumberByTable: [],
        searchParams: { textSearch: '' },
      });
    },
    SET_HOME_SCREEN_SEARCH_IS_EXACT: ({ isExact }: { isExact: boolean }) => {
      this.state.set('isExact', isExact);
    },
    SET_HOME_SCREEN_TRAINING_PROMPT_DISPLAY: ({
      trainingPromptDisplay,
    }: {
      trainingPromptDisplay: TrainingPromptDisplay;
    }) => {
      this.state.set('trainingPromptDisplay', trainingPromptDisplay);
    },
    SET_HOME_SCREEN_DISPLAYED_ADDON_IDENTIFIER: ({
      displayedAddOnIdentifier,
    }: {
      displayedAddOnIdentifier: string;
    }) => {
      this.state.set('displayedAddOnIdentifier', displayedAddOnIdentifier);
    },
    SET_HOME_SCREEN_CUSTOM_LINKS_EXPANDED: ({ customLinksExpanded }: { customLinksExpanded: boolean }) => {
      this.state.set('customLinksExpanded', customLinksExpanded);
    },
    SET_BRANCH_CODE: ({ branchCode }: { branchCode: string | null }) => {
      this.state.set('branchCode', branchCode);
    },
  };

  /**
   * Exports state so it can be used to re-create the state later using `rehydrate`.
   *
   * @returns {Object} The dehydrated state.
   */
  dehydrate(): HomeScreenDehydratedState {
    return _.pick(this.state.serialize(), ['pageSizeByTable', 'trainingPromptDisplay', 'customLinksExpanded']) as Pick<
      HomeScreenState,
      'pageSizeByTable' | 'trainingPromptDisplay' | 'customLinksExpanded'
    >;
  }

  /**
   * Re-creates the workbench state.
   *
   * @param {Object} dehydratedState - Previous state usually obtained from `dehydrate` method.
   * @return {Object} A promise that is fulfilled when the workbench is completely rehydrated.
   */
  rehydrate(dehydratedState: Partial<HomeScreenDehydratedState>) {
    this.state.merge({
      sortByTable: [],
      ..._.omitBy(
        _.pick(dehydratedState, ['pageSizeByTable', 'trainingPromptDisplay', 'customLinksExpanded']),
        _.isUndefined,
      ),
    });
  }
}
